import { FC, useContext, useEffect, useState } from "react";
import { FlyoutContext, FlyoutFooter } from "@app/shared/Flyouts";
import { toast } from "react-toastify";
import { QESButton } from "@app/shared/qes-standards";
import ManageFilterSetRow from "@app/shared/manage-saved-filters/ManageFilterSetRow";

interface ManageSavedFiltersFlyoutProps {
  onSubmit: (filtersToDelete: Set<string>, filterNameUpdates: Map<string, string>) => void;
  loadedFilters: string[];
}

const ManageSavedFiltersFlyout: FC<ManageSavedFiltersFlyoutProps> = ({
                                                                       onSubmit,
                                                                       loadedFilters
                                                                     }: ManageSavedFiltersFlyoutProps) => {
  const flyoutContext = useContext(FlyoutContext);
  const [addingNewFilter, setAddingNewFilter] = useState(false);
  const [newFilterNames, setNewFilterNames] = useState(new Map<string, string>());
  const [filtersToDelete, setFiltersToDelete] = useState(new Set<string>());

  // If the user cancels saving the current filter set, remove it from the
  // list of newFilterNames so it won't get saved
  useEffect(() => {
    if (!addingNewFilter) {
      const newNewFilterNames = new Map(newFilterNames);
      newNewFilterNames.delete("");
      setNewFilterNames(newNewFilterNames);
    }
  }, [addingNewFilter]);

  const toggleFilterDeletion = ((filterName: string) => {
    const newFiltersToDelete = new Set(filtersToDelete);
    if (filtersToDelete.has(filterName)) {
      newFiltersToDelete.delete(filterName);
    } else {
      newFiltersToDelete.add(filterName);
    }
    setFiltersToDelete(newFiltersToDelete);
  });

  const updateFilterName = (oldName: string, newName: string) => {
    const newNewFilterNames = new Map(newFilterNames);
    newNewFilterNames.set(oldName, newName);
    setNewFilterNames(newNewFilterNames);
  }

  const filtersList = () => {
    return loadedFilters.map((filterSetName, index) => {
      return (
        <li key={filterSetName}>
          <ManageFilterSetRow
            filterName={filterSetName}
            index={index}
            newName={newFilterNames.get(filterSetName)}
            isMarkedForDeletion={filtersToDelete.has(filterSetName)}
            toggleFilterDeletion={toggleFilterDeletion}
            updateFilterName={updateFilterName}
          />
        </li>
      )
    });
  };

  const wrappedOnSubmit = () => {
    onSubmit(filtersToDelete, newFilterNames);
    toast(<span>Your saved filters have been updated</span>,
      {
        containerId: "z-index-9999",
        onOpen: async () => {
          await flyoutContext.handleCloseFlyout();
        },
        type: "success",
      },
    );
  }

  return (
    <>
      <ul className="list-unstyled">
        <li>
          {!addingNewFilter ? (
            <QESButton
              qesType="secondary"
              qesTestId="manage-saved-filters-flyout__add"
              onClick={() => setAddingNewFilter(true)}
              className="mb-3"
            >
              + Save Currently Applied Filters
            </QESButton>
          ) : (
            <ManageFilterSetRow
              filterName=""
              index={-1}
              newName={newFilterNames.get("")}
              isMarkedForDeletion={false}
              toggleFilterDeletion={() => {
                setAddingNewFilter(false);
              }}
              updateFilterName={updateFilterName}
            />
          )}
        </li>
        {filtersList()}
      </ul>
      <FlyoutFooter
        submitButton={
          <QESButton
            qesType="primary"
            qesTestId="manage-saved-filters-flyout__load"
            onClick={wrappedOnSubmit}
          >
            Save Changes
          </QESButton>
        }
      />
    </>
  );
}

export default ManageSavedFiltersFlyout;
